import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Search, Lightbulb, Rocket, TrendingUp, Zap } from "lucide-react";

const BrandifyProcess = () => {
  const steps = [
    {
      number: "01",
      title: "DISCOVER",
      icon: <Search className="w-12 h-12" />,
      description: "We dive deep into your brand, market, and competition. Understanding your goals and challenges is the foundation of everything we do.",
      details: [
        "Brand audit & analysis",
        "Market research",
        "Competitor analysis",
        "Audience profiling"
      ]
    },
    {
      number: "02",
      title: "STRATEGIZE",
      icon: <Lightbulb className="w-12 h-12" />,
      description: "Armed with insights, we craft a comprehensive strategy that aligns with your business objectives and market opportunities.",
      details: [
        "Strategic positioning",
        "Channel selection",
        "Content strategy",
        "KPI definition"
      ]
    },
    {
      number: "03",
      title: "BUILD",
      icon: <Rocket className="w-12 h-12" />,
      description: "We bring the strategy to life with compelling creative, powerful technology, and meticulously planned campaigns.",
      details: [
        "Creative development",
        "Tech implementation",
        "Campaign setup",
        "Asset creation"
      ]
    },
    {
      number: "04",
      title: "SCALE",
      icon: <TrendingUp className="w-12 h-12" />,
      description: "Launch, monitor, and scale what works. We continuously expand successful campaigns while maintaining quality and efficiency.",
      details: [
        "Campaign launch",
        "Performance monitoring",
        "Budget scaling",
        "Channel expansion"
      ]
    },
    {
      number: "05",
      title: "OPTIMIZE",
      icon: <Zap className="w-12 h-12" />,
      description: "Powered by AI, we never stop improving. Every data point informs decisions that make your marketing more effective.",
      details: [
        "A/B testing",
        "AI-powered analysis",
        "Continuous refinement",
        "ROI maximization"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#030303] brandify-body">
      <SEO brand="brandify" path="/brandify/process" title="Process" description="How Brandify turns research into strategy into growth — our AI-augmented process." />
      <Navigation brand="brandify" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="heading-font text-5xl md:text-7xl text-white mb-6" data-testid="process-heading">
              OUR
              <span className="text-[#1cb5ae]"> PROCESS</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              A proven methodology that combines strategic thinking, creative excellence, and AI-powered optimization.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="mb-16 last:mb-0"
            >
              <div className="glass-effect rounded-3xl p-8 md:p-12 hover:bg-white/10 transition-all">
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 rounded-full bg-[#1cb5ae]/20 flex items-center justify-center text-[#1cb5ae] mb-4">
                      {step.icon}
                    </div>
                  </div>
                  
                  <div className="flex-1">
                    <div className="text-6xl font-bold text-[#1cb5ae]/10 mb-2">{step.number}</div>
                    <h2 className="heading-font text-3xl md:text-4xl text-white mb-4">
                      {step.title}
                    </h2>
                    <p className="text-lg text-gray-300 mb-6">
                      {step.description}
                    </p>
                    <ul className="grid md:grid-cols-2 gap-3">
                      {step.details.map((detail, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-400">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#1cb5ae]"></div>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
              
              {index < steps.length - 1 && (
                <div className="flex justify-center my-8">
                  <div className="w-0.5 h-12 bg-gradient-to-b from-[#1cb5ae] to-transparent"></div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Why It Works */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="heading-font text-4xl md:text-5xl text-white mb-6">
              WHY IT WORKS
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our process isn't just theory—it's battle-tested across dozens of brands and industries.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Data-First Approach",
                description: "Every decision is backed by real data, not assumptions. We test, measure, and optimize continuously."
              },
              {
                title: "AI-Powered Efficiency",
                description: "Artificial intelligence handles repetitive tasks, freeing our team to focus on strategy and creativity."
              },
              {
                title: "Agile Methodology",
                description: "We move fast and adapt quickly. Market conditions change—our strategies evolve with them."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-effect rounded-2xl p-8"
              >
                <h3 className="text-2xl font-semibold text-white mb-4">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer brand="brandify" />
    </div>
  );
};

export default BrandifyProcess;